/**
 * Marks the measurements inside a passage of prose.
 *
 * A patient handed six paragraphs of clinical writing reads the first line and
 * stops. What they actually need from those paragraphs is the numbers — the
 * angle that was out of range, the one that was not — and those are already in
 * the text. So they are marked where they stand.
 *
 * Deliberately only measurements. It would be easy to also bold words like
 * "significant" or "normal", and it would be wrong: "not normal" would then
 * skim as its own opposite, and a highlight that misleads is worse than none.
 * A number with a unit cannot be reversed by the word in front of it.
 *
 * Nothing is restated or recomputed here, so a marked passage can never
 * disagree with the tables above it.
 */

/** Degrees, percentages, pressures, lengths, masses and BMI figures. */
const MEASUREMENT = /((?:BMI\s+(?:of\s+)?)?\d+(?:\.\d+)?\s*(?:°|%|kPa|cm²|cm|kg|mm)|BMI\s+(?:of\s+)?\d+(?:\.\d+)?)/g;

/**
 * Splits a line into plain runs and measurement runs.
 *
 * Returns `[{ text, key }]` so a React renderer and a string builder can both
 * use it without either of them owning the pattern.
 */
export function splitMeasurements(text) {
  const out = [];
  let last = 0;
  const source = String(text ?? '');

  for (const match of source.matchAll(MEASUREMENT)) {
    if (match.index > last) out.push({ text: source.slice(last, match.index), key: false });
    out.push({ text: match[0], key: true });
    last = match.index + match[0].length;
  }
  if (last < source.length) out.push({ text: source.slice(last), key: false });
  return out;
}

/**
 * Splits a passage into sentences, keeping every character.
 *
 * A terminator only ends a sentence when whitespace or the end of the passage
 * follows it. That one condition is what keeps "24.1°" intact: the first
 * version of this split on any full stop and cut the decimal in half, so every
 * reading in a highlighted line was torn into "24." and "1°".
 *
 * Still deliberately crude in the other direction — "Mr. Mahajan" does split,
 * costing a highlight its opening word at worst. A clinical report does not
 * need a sentence tokeniser to decide where to put a background colour.
 */
export function splitSentences(text) {
  return String(text ?? '').match(/[\s\S]*?[.!?](?=\s|$)|[\s\S]+$/g) ?? [];
}

/**
 * Does this sentence state one of the readings the summary table flagged?
 *
 * Matched on the number, never on a word. The findings list already decided
 * what is outside range; this only finds where the passage talks about it, so
 * a highlighted line can never claim something the tables do not.
 */
export function statesFinding(sentence, degrees) {
  if (!degrees?.length) return false;
  for (const match of String(sentence ?? '').matchAll(/(\d+(?:\.\d+)?)\s*°/g)) {
    const value = Number(match[1]);
    if (degrees.some(d => Math.abs(d - value) < 0.05)) return true;
  }
  return false;
}

export default splitMeasurements;
